// SCR-STAFF-415 (بخش صندوق‌ها؛ US-BIL-040): تعریف و ویرایش صندوق شعبه با نام، روش‌های مجاز فاز 1a (C-AGENT-555)، پایانه‌های کارتخوان، حساب واریز
// و سقف نقد؛ غیرفعال کردن صندوق (bil.cash_register.manage — C-AGENT-564). AP-BIL-04: ایستگاه عامل محلی کارتخوان متصل (US-BIL-032).
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type BilCashRegisterView, type BilPaymentMethod } from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, FormDialog, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useActiveBranch } from '../scheduling/common';
import { parseRials } from './CashierPage';
import { Amount, bilKeys, rial } from './common';

const PHASE_ONE: readonly BilPaymentMethod[] = ['Cash', 'CardPos', 'OnlineGateway', 'BankTransfer', 'Wallet'];

interface Draft {
  id: string | null;
  branchId: string;
  name: string;
  methods: BilPaymentMethod[];
  terminals: string;
  depositAccount: string;
  cashLimit: string;
  isActive: boolean;
  workstationId: string;
}

export function CashRegistersSection() {
  const { t } = useTranslation('bil');
  const { tenantApi, session, activeTenant } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const { branchId } = useActiveBranch();
  const branches = activeTenant?.branches ?? [];
  const [draft, setDraft] = useState<Draft | null>(null);
  const registers = useQuery({ queryKey: bilKeys.registers(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/cash-registers')) });
  const save = useMutation({
    mutationFn: (value: Draft) => {
      const body = {
        branchId: value.branchId,
        name: value.name.trim(),
        allowedMethods: value.methods,
        posTerminals: value.terminals.split(/[,\u060C\s]+/).map((item) => item.trim()).filter(Boolean),
        depositAccount: value.depositAccount.trim() || null,
        cashLimit: value.cashLimit.trim() ? rial(parseRials(value.cashLimit)) : null,
        isActive: value.isActive,
        workstationId: value.workstationId.trim() || null,
      };
      return value.id
        ? unwrap(tenantApi.PUT('/api/v1/billing/cash-registers/{id}', { params: { path: { id: value.id } }, body }))
        : unwrap(tenantApi.POST('/api/v1/billing/cash-registers', { body, headers: { 'Idempotency-Key': key.current() } }));
    },
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: bilKeys.all(session?.tenantId) });
    },
  });
  const edit = (row: BilCashRegisterView) =>
    setDraft({
      id: row.id,
      branchId: row.branchId,
      name: row.name,
      methods: [...row.allowedMethods],
      terminals: row.posTerminals.join(', '),
      depositAccount: row.depositAccount ?? '',
      cashLimit: row.cashLimit ? String(row.cashLimit.amount) : '',
      isActive: row.isActive,
      workstationId: row.workstationId ?? '',
    });
  const columns: DataTableColumn<BilCashRegisterView>[] = [
    { id: 'name', headerKey: 'bil:registers.name', cell: (row) => row.name },
    { id: 'branch', headerKey: 'bil:registers.branch', cell: (row) => row.branchName ?? '—' },
    { id: 'methods', headerKey: 'bil:registers.methods', cell: (row) => row.allowedMethods.map((method) => t(`bil:method.${method}`)).join(' · ') },
    { id: 'limit', headerKey: 'bil:registers.cash_limit', cell: (row) => <Amount money={row.cashLimit} /> },
    { id: 'active', headerKey: 'bil:registers.active', cell: (row) => t(row.isActive ? 'bil:registers.yes' : 'bil:registers.no') },
  ];
  return (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1">{t('bil:registers.title')}</Typography>
        <Button
          onClick={() => setDraft({ id: null, branchId: branchId ?? branches[0]?.branchId ?? '', name: '', methods: [...PHASE_ONE], terminals: '', depositAccount: '', cashLimit: '', isActive: true, workstationId: '' })}
          data-testid="bil-register-add"
        >
          {t('bil:registers.add')}
        </Button>
      </Stack>
      <DataTable
        captionKey="bil:registers.title"
        columns={columns}
        rows={registers.data?.items}
        getRowId={(row) => row.id}
        isLoading={registers.isPending}
        error={registers.error}
        onRetry={() => void registers.refetch()}
        emptyTitleKey="bil:registers.empty"
        onRowClick={edit}
      />
      <FormDialog
        open={draft !== null}
        titleKey={draft?.id ? 'bil:registers.edit' : 'bil:registers.add'}
        onSubmit={() => draft && save.mutate(draft)}
        onClose={() => setDraft(null)}
        isDirty
        isSubmitting={save.isPending}
        submitError={save.error}
      >
        {draft ? (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField required label={t('bil:registers.name')} value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} slotProps={{ htmlInput: { 'data-testid': 'bil-register-name' } }} />
            <TextField select label={t('bil:registers.branch')} value={draft.branchId} onChange={(event) => setDraft({ ...draft, branchId: event.target.value })} disabled={draft.id !== null}>
              {branches.map((branch) => (
                <MenuItem key={branch.branchId} value={branch.branchId}>
                  {branch.name}
                </MenuItem>
              ))}
            </TextField>
            <FormGroup row>
              {PHASE_ONE.map((method) => (
                <FormControlLabel
                  key={method}
                  control={
                    <Checkbox
                      checked={draft.methods.includes(method)}
                      onChange={(event) => setDraft({ ...draft, methods: event.target.checked ? [...draft.methods, method] : draft.methods.filter((item) => item !== method) })}
                    />
                  }
                  label={t(`bil:method.${method}`)}
                />
              ))}
            </FormGroup>
            <TextField label={t('bil:registers.terminals')} value={draft.terminals} onChange={(event) => setDraft({ ...draft, terminals: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr' } }} />
            <TextField label={t('bil:registers.deposit_account')} value={draft.depositAccount} onChange={(event) => setDraft({ ...draft, depositAccount: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr' } }} />
            <TextField label={t('bil:registers.cash_limit')} value={draft.cashLimit} onChange={(event) => setDraft({ ...draft, cashLimit: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric' } }} />
            <TextField
              label={t('bil:registers.workstation')}
              helperText={t('bil:registers.workstation_help')}
              value={draft.workstationId}
              onChange={(event) => setDraft({ ...draft, workstationId: event.target.value })}
              slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'bil-register-workstation' } }}
            />
            <FormControlLabel control={<Switch checked={draft.isActive} onChange={(event) => setDraft({ ...draft, isActive: event.target.checked })} />} label={t('bil:registers.active')} />
          </Stack>
        ) : null}
      </FormDialog>
    </Stack>
  );
}
